/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  StyleSheet,
  SafeAreaView,
  StatusBar,
  Alert,
  Dimensions,
  Animated,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Ionicons, MaterialCommunityIcons, FontAwesome5 } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import { LinearGradient } from 'expo-linear-gradient';

const { width } = Dimensions.get('window');

const CourseLearningScreen = ({ route }) => {
  const navigation = useNavigation();
  const { course, courseId } = route?.params || {};
  const [userData, setUserData] = useState(null);
  const [currentLesson, setCurrentLesson] = useState(0);
  const [completedLessons, setCompletedLessons] = useState([]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [fadeAnim] = useState(new Animated.Value(0));
  const [slideAnim] = useState(new Animated.Value(50));
  const [scaleAnim] = useState(new Animated.Value(0.8));

  // Enhanced gradient color schemes
  const gradients = {
    primary: ['#667eea', '#764ba2'],
    secondary: ['#f093fb', '#f5576c'],
    success: ['#4facfe', '#00f2fe'],
    warning: ['#43e97b', '#38f9d7'],
    danger: ['#fa709a', '#fee140'],
    info: ['#a8edea', '#fed6e3'],
    purple: ['#d299c2', '#fef9d7'],
    orange: ['#fdbb2d', '#22c1c3'],
    blue: ['#2196f3', '#21cbf3'],
    green: ['#56ab2f', '#a8e6cf'],
    pink: ['#ff9a9e', '#fecfef'],
    teal: ['#667eea', '#764ba2'],
    gold: ['#f7971e', '#ffd200'],
    project: ['#ff6b6b', '#feca57'],
  };

  // Enhanced mixed color scheme for different course categories
  const categoryColors = {
    'Civil Engineering': gradients.orange,
    'Mechanical Engineering': gradients.green,
    'Electrical Engineering': gradients.blue,
    'Software Engineering': gradients.purple,
    'Chemical Engineering': gradients.danger,
    'Environmental Engineering': gradients.info,
    'Project Management': gradients.project,
    'Materials Science': gradients.teal,
    'Biomedical Engineering': gradients.warning,
    'Aerospace Engineering': gradients.primary,
  };

  const getGradientForCategory = (category) => {
    return categoryColors[category] || gradients.primary;
  };

  useEffect(() => {
    loadUserData();
    loadProgress();
    startAnimations();
  }, []);

  const startAnimations = () => {
    Animated.parallel([
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 1000,
        useNativeDriver: true,
      }),
      Animated.timing(slideAnim, {
        toValue: 0,
        duration: 800,
        useNativeDriver: true,
      }),
      Animated.timing(scaleAnim, {
        toValue: 1,
        duration: 1200,
        useNativeDriver: true,
      }),
    ]).start();
  };

  const loadUserData = async () => {
    try {
      const storedUser = await AsyncStorage.getItem('user');
      const storedUserData = await AsyncStorage.getItem('userData');
      
      if (storedUser) {
        const parsedUser = JSON.parse(storedUser);
        setUserData(parsedUser);
      } else if (storedUserData) {
        const parsedUserData = JSON.parse(storedUserData);
        setUserData(parsedUserData);
      }
    } catch (error) {
      console.error('Error loading user data:', error);
    }
  };

  const loadProgress = async () => {
    try {
      const courseKey = `course_progress_${course?.id || courseId || 'default'}`;
      const storedProgress = await AsyncStorage.getItem(courseKey);
      if (storedProgress) {
        const progress = JSON.parse(storedProgress);
        setCurrentLesson(progress.currentLesson || 0);
        setCompletedLessons(progress.completedLessons || []);
      }
    } catch (error) {
      console.error('Error loading progress:', error);
    }
  };

  const saveProgress = async () => {
    try {
      const courseKey = `course_progress_${course?.id || courseId || 'default'}`;
      const progress = {
        currentLesson,
        completedLessons,
        lastAccessed: new Date().toISOString(),
      };
      await AsyncStorage.setItem(courseKey, JSON.stringify(progress));
    } catch (error) {
      console.error('Error saving progress:', error);
    }
  };

  // Enhanced sample course data with Project Management course
  const courseData = {
    id: course?.id || courseId || 'pm-101',
    title: course?.title || 'Project Management Fundamentals',
    instructor: course?.instructor || 'Dr. Michael Chen, PMP',
    category: course?.category || 'Project Management',
    gradient: getGradientForCategory(course?.category || 'Project Management'),
    icon: course?.icon || 'clipboard-check',
    progress: course?.progress || 0,
    modules: course?.modules || [
      {
        id: '1',
        title: 'Introduction to Project Management',
        duration: '3 hours',
        gradient: gradients.project,
        lessons: [
          {
            id: '1-1',
            title: 'What is Project Management?',
            duration: '20 min',
            type: 'video',
            completed: false,
            description: '🎯 Learn the fundamentals of project management and its role in modern organizations. Understand key concepts, terminology, and the value of structured project approaches.'
          },
          {
            id: '1-2',
            title: 'Project vs Operations',
            duration: '15 min',
            type: 'video',
            completed: false,
            description: '🔄 Distinguish between projects and ongoing operations. Learn when to apply project management methodologies and when operational processes are more appropriate.'
          },
          {
            id: '1-3',
            title: 'Project Life Cycle Overview',
            duration: '25 min',
            type: 'video',
            completed: false,
            description: '📊 Explore the typical phases of a project from initiation to closure. Understand the key activities, deliverables, and stakeholders involved in each phase.'
          },
          {
            id: '1-4',
            title: 'Quiz: PM Fundamentals',
            duration: '15 min',
            type: 'quiz',
            completed: false,
            description: '✅ Test your understanding of basic project management concepts and terminology.'
          }
        ]
      },
      {
        id: '2',
        title: 'Project Planning & Scope Management',
        duration: '4 hours',
        gradient: gradients.blue,
        lessons: [
          {
            id: '2-1',
            title: 'Defining Project Scope',
            duration: '30 min',
            type: 'video',
            completed: false,
            description: '📋 Learn how to clearly define project boundaries, deliverables, and success criteria. Master the art of scope definition to prevent scope creep.'
          },
          {
            id: '2-2',
            title: 'Work Breakdown Structure (WBS)',
            duration: '35 min',
            type: 'video',
            completed: false,
            description: '🏗️ Create comprehensive work breakdown structures to organize project work into manageable components. Learn WBS best practices and common pitfalls.'
          },
          {
            id: '2-3',
            title: 'Project Scheduling Techniques',
            duration: '40 min',
            type: 'video',
            completed: false,
            description: '⏰ Master critical path method (CPM), PERT, and Gantt charts. Learn to create realistic project schedules and manage dependencies.'
          },
          {
            id: '2-4',
            title: 'Resource Planning & Allocation',
            duration: '25 min',
            type: 'video',
            completed: false,
            description: '👥 Plan and allocate human, financial, and material resources effectively. Learn resource leveling and optimization techniques.'
          },
          {
            id: '2-5',
            title: 'Exercise: Create Project Plan',
            duration: '30 min',
            type: 'exercise',
            completed: false,
            description: '📝 Hands-on exercise to create a complete project plan including scope, WBS, schedule, and resource allocation.'
          }
        ]
      },
      {
        id: '3',
        title: 'Risk Management & Quality Control',
        duration: '3.5 hours',
        gradient: gradients.warning,
        lessons: [
          {
            id: '3-1',
            title: 'Risk Identification & Assessment',
            duration: '30 min',
            type: 'video',
            completed: false,
            description: '⚠️ Learn systematic approaches to identify, analyze, and prioritize project risks. Master risk probability and impact assessment techniques.'
          },
          {
            id: '3-2',
            title: 'Risk Response Strategies',
            duration: '25 min',
            type: 'video',
            completed: false,
            description: '🛡️ Develop effective risk response strategies: avoid, mitigate, transfer, and accept. Learn when to apply each strategy for maximum effectiveness.'
          },
          {
            id: '3-3',
            title: 'Quality Planning & Assurance',
            duration: '35 min',
            type: 'video',
            completed: false,
            description: '✨ Establish quality standards, metrics, and control processes. Learn quality planning tools and techniques for consistent deliverable quality.'
          },
          {
            id: '3-4',
            title: 'Quality Control & Improvement',
            duration: '30 min',
            type: 'video',
            completed: false,
            description: '🔍 Implement quality control processes, conduct quality audits, and drive continuous improvement throughout the project lifecycle.'
          },
          {
            id: '3-5',
            title: 'Case Study: Risk & Quality',
            duration: '25 min',
            type: 'project',
            completed: false,
            description: '📊 Analyze a real-world project scenario to identify risks and develop quality management strategies.'
          }
        ]
      },
      {
        id: '4',
        title: 'Team Leadership & Communication',
        duration: '3 hours',
        gradient: gradients.success,
        lessons: [
          {
            id: '4-1',
            title: 'Building High-Performance Teams',
            duration: '30 min',
            type: 'video',
            completed: false,
            description: '👥 Learn team development stages, motivation techniques, and strategies for building cohesive, high-performing project teams.'
          },
          {
            id: '4-2',
            title: 'Effective Communication Strategies',
            duration: '25 min',
            type: 'video',
            completed: false,
            description: '💬 Master project communication planning, stakeholder engagement, and conflict resolution techniques for successful project delivery.'
          },
          {
            id: '4-3',
            title: 'Leadership Styles in Projects',
            duration: '30 min',
            type: 'video',
            completed: false,
            description: '👑 Explore different leadership styles and learn when to apply each approach based on team maturity, project phase, and organizational culture.'
          },
          {
            id: '4-4',
            title: 'Stakeholder Management',
            duration: '35 min',
            type: 'video',
            completed: false,
            description: '🤝 Identify, analyze, and engage project stakeholders effectively. Learn stakeholder mapping and engagement strategies.'
          },
          {
            id: '4-5',
            title: 'Quiz: Leadership & Communication',
            duration: '20 min',
            type: 'quiz',
            completed: false,
            description: '📋 Assess your knowledge of team leadership and communication best practices in project management.'
          }
        ]
      },
      {
        id: '5',
        title: 'Agile & Modern PM Methodologies',
        duration: '4 hours',
        gradient: gradients.purple,
        lessons: [
          {
            id: '5-1',
            title: 'Introduction to Agile Project Management',
            duration: '30 min',
            type: 'video',
            completed: false,
            description: '🚀 Understand Agile principles, values, and methodologies. Learn when to use Agile vs traditional project management approaches.'
          },
          {
            id: '5-2',
            title: 'Scrum Framework Deep Dive',
            duration: '40 min',
            type: 'video',
            completed: false,
            description: '🏃‍♂️ Master Scrum roles, events, and artifacts. Learn to implement Scrum effectively for software and non-software projects.'
          },
          {
            id: '5-3',
            title: 'Kanban & Lean Principles',
            duration: '35 min',
            type: 'video',
            completed: false,
            description: '📋 Implement Kanban boards and Lean principles to optimize workflow, reduce waste, and improve project delivery efficiency.'
          },
          {
            id: '5-4',
            title: 'Hybrid Project Management',
            duration: '30 min',
            type: 'video',
            completed: false,
            description: '🔄 Learn to combine traditional and Agile approaches for optimal project outcomes. Understand when and how to apply hybrid methodologies.'
          },
          {
            id: '5-5',
            title: 'Digital PM Tools & Technologies',
            duration: '25 min',
            type: 'video',
            completed: false,
            description: '💻 Explore modern project management software, collaboration tools, and digital technologies that enhance project delivery.'
          },
          {
            id: '5-6',
            title: 'Final Project: PM Methodology Selection',
            duration: '40 min',
            type: 'project',
            completed: false,
            description: '🎯 Capstone project to analyze a complex scenario and recommend the most appropriate project management methodology.'
          }
        ]
      }
    ]
  };

  // Safe array operations with proper null checks
  const allLessons = (courseData.modules || []).flatMap(module => 
    (module.lessons || []).map(lesson => ({
      ...lesson,
      moduleTitle: module.title,
      moduleGradient: module.gradient || courseData.gradient
    }))
  );

  const currentLessonData = allLessons[currentLesson] || {};

  const handlePlayPause = () => {
    setIsPlaying(!isPlaying);
    if (currentLessonData?.type === 'video') {
      Alert.alert(
        isPlaying ? '⏸️ Video Paused' : '▶️ Video Playing',
        `${isPlaying ? 'Paused' : 'Playing'}: ${currentLessonData.title || 'Current lesson'}`
      );
    } else if (currentLessonData?.type === 'quiz') {
      Alert.alert('📝 Interactive Quiz', 'Starting quiz session...');
    } else if (currentLessonData?.type === 'exercise') {
      Alert.alert('💪 Practice Exercise', 'Opening practice problems...');
    } else if (currentLessonData?.type === 'project') {
      Alert.alert('🎯 Project Assignment', 'Loading project requirements...');
    }
  };

  const handleNextLesson = () => {
    if (currentLesson < allLessons.length - 1) {
      // Mark current lesson as completed
      if (currentLessonData?.id && !completedLessons.includes(currentLessonData.id)) {
        const newCompleted = [...completedLessons, currentLessonData.id];
        setCompletedLessons(newCompleted);
      }
      setCurrentLesson(currentLesson + 1);
      setIsPlaying(false);
      saveProgress();
    } else {
      Alert.alert(
        '🎉 Course Complete!', 
        'Congratulations! You have completed all lessons in this Project Management course.',
        [
          { text: '🏆 View Certificate', onPress: () => navigation.navigate('LearningCertificateScreen') },
          { text: '📚 Continue Learning', onPress: () => navigation.navigate('LearningCoursesScreen') }
        ]
      );
    }
  };

  const handlePreviousLesson = () => {
    if (currentLesson > 0) {
      setCurrentLesson(currentLesson - 1);
      setIsPlaying(false);
      saveProgress();
    }
  };

  const handleLessonSelect = (index) => {
    if (index >= 0 && index < allLessons.length) {
      setCurrentLesson(index);
      setIsPlaying(false);
      saveProgress();
    }
  };

  const handleMarkComplete = () => {
    if (currentLessonData?.id && !completedLessons.includes(currentLessonData.id)) {
      const newCompleted = [...completedLessons, currentLessonData.id];
      setCompletedLessons(newCompleted);
      saveProgress();
      Alert.alert('✅ Lesson Completed!', `"${currentLessonData.title}" marked as complete.`);
    } else {
      Alert.alert('✅ Already Completed', 'This lesson is already marked as complete.');
    }
  };

  const getProgressPercentage = () => {
    if (allLessons.length === 0) return 0;
    return Math.round((completedLessons.length / allLessons.length) * 100);
  };

  const getLessonIcon = (type) => {
    switch (type) {
      case 'video':
        return 'play-circle';
      case 'quiz':
        return 'help-circle';
      case 'exercise':
        return 'pencil';
      case 'project':
        return 'briefcase';
      default:
        return 'book-open';
    }
  };

  const getLessonTypeGradient = (type) => {
    switch (type) {
      case 'video':
        return gradients.orange;
      case 'quiz':
        return gradients.gold;
      case 'exercise':
        return gradients.success;
      case 'project':
        return gradients.purple;
      default:
        return gradients.blue;
    }
  };

  const getUserName = () => {
    if (!userData) return 'Student';
    return userData.full_name || userData.fullName || userData.name || 
           `${userData.first_name || userData.firstName || ''} ${userData.last_name || userData.lastName || ''}`.trim() || 
           'Student';
  };

  return (
    <SafeAreaView style={styles.safeArea}>
      <StatusBar barStyle="light-content" backgroundColor="#ff6b6b" />
      
      {/* Enhanced Header */}
      <LinearGradient
        colors={courseData.gradient}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 0 }}
        style={styles.header}
      >
        <TouchableOpacity
          onPress={() => navigation.goBack()}
          style={styles.backButton}
        >
          <Ionicons name="arrow-back" size={28} color="#ffffff" />
        </TouchableOpacity>
        <View style={styles.headerInfo}>
          <Text style={styles.headerTitle} numberOfLines={1}>
            📊 {courseData.title}
          </Text>
          <Text style={styles.headerSubtitle}>
            Lesson {currentLesson + 1} of {allLessons.length} • {courseData.instructor}
          </Text>
        </View>
        <TouchableOpacity 
          style={styles.menuButton}
          onPress={() => navigation.navigate('LearningDashboard')}
        >
          <MaterialCommunityIcons name="view-dashboard" size={28} color="#ffffff" />
        </TouchableOpacity>
      </LinearGradient>

      {/* Enhanced Progress Bar */}
      <Animated.View 
        style={[
          styles.progressContainer,
          {
            opacity: fadeAnim,
            transform: [{ scale: scaleAnim }],
          },
        ]}
      >
        <LinearGradient
          colors={courseData.gradient}
          style={styles.progressGradient}
        >
          <View style={styles.progressInfo}>
            <Text style={styles.progressLabel}>📈 Course Progress</Text>
            <Text style={styles.progressPercentage}>
              {getProgressPercentage()}%
            </Text>
          </View>
          <View style={styles.progressBar}>
            <LinearGradient
              colors={['#ffffff', '#f0f9ff']}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 0 }}
              style={[styles.progressFill, { width: `${getProgressPercentage()}%` }]}
            />
          </View>
          <Text style={styles.progressText}>
            ✅ {completedLessons.length} of {allLessons.length} lessons completed
          </Text>
        </LinearGradient>
      </Animated.View>

      <ScrollView style={styles.container} showsVerticalScrollIndicator={false}>
        {/* Enhanced Welcome Message */}
        <Animated.View 
          style={[
            styles.welcomeCard,
            {
              opacity: fadeAnim,
              transform: [{ translateY: slideAnim }],
            },
          ]}
        >
          <LinearGradient
            colors={gradients.info}
            style={styles.welcomeGradient}
          >
            <MaterialCommunityIcons name="account-circle" size={32} color="#ffffff" />
            <Text style={styles.welcomeText}>🎯 Welcome back, {getUserName()}!</Text>
            <Text style={styles.welcomeSubtext}>Continue mastering Project Management skills</Text>
          </LinearGradient>
        </Animated.View>

        {/* Enhanced Current Lesson Card */}
        <Animated.View 
          style={[
            styles.currentLessonCard,
            {
              opacity: fadeAnim,
              transform: [{ translateY: slideAnim }],
            },
          ]}
        >
          <LinearGradient
            colors={currentLessonData?.moduleGradient || courseData.gradient}
            style={styles.lessonHeaderGradient}
          >
            <View style={styles.lessonHeader}>
              <View style={styles.lessonIconContainer}>
                <MaterialCommunityIcons 
                  name={getLessonIcon(currentLessonData?.type)} 
                  size={36} 
                  color="#ffffff" 
                />
              </View>
              <View style={styles.lessonInfo}>
                <Text style={styles.moduleTitle}>📚 {currentLessonData?.moduleTitle || 'Module'}</Text>
                <Text style={styles.lessonTitle}>
                  {currentLessonData?.title || 'Lesson Title'}
                </Text>
                <Text style={styles.lessonDuration}>⏱️ {currentLessonData?.duration || 'Duration'}</Text>
              </View>
              <View style={styles.lessonBadge}>
                <Text style={styles.lessonBadgeText}>
                  {currentLessonData?.type?.toUpperCase() || 'LESSON'}
                </Text>
              </View>
            </View>
          </LinearGradient>

          {/* Enhanced Lesson Description */}
          <View style={styles.lessonContent}>
            <Text style={styles.lessonDescription}>
              {currentLessonData?.description || 'Lesson description will appear here.'}
            </Text>

            {/* Enhanced Video Player Placeholder */}
            <View style={styles.videoPlayer}>
              <LinearGradient
                colors={['#1f2937', '#374151']}
                style={styles.videoPlayerGradient}
              >
                <TouchableOpacity 
                  style={styles.playButtonContainer}
                  onPress={handlePlayPause}
                >
                  <LinearGradient
                    colors={courseData.gradient}
                    style={styles.playButton}
                  >
                    <MaterialCommunityIcons 
                      name={isPlaying ? "pause" : "play"} 
                      size={48} 
                      color="#ffffff" 
                    />
                  </LinearGradient>
                </TouchableOpacity>
                <Text style={styles.videoTitle}>
                  {currentLessonData?.type === 'video' ? '🎥 Video Lesson' : 
                   currentLessonData?.type === 'quiz' ? '📝 Interactive Quiz' :
                   currentLessonData?.type === 'exercise' ? '💪 Practice Exercise' :
                   currentLessonData?.type === 'project' ? '🎯 Project Assignment' :
                   '📖 Learning Content'}
                </Text>
                <Text style={styles.videoSubtitle}>
                  {isPlaying ? '▶️ Playing...' : '👆 Tap to start'}
                </Text>
              </LinearGradient>
            </View>

            {/* Enhanced Lesson Controls */}
            <View style={styles.lessonControls}>
              <TouchableOpacity 
                style={styles.controlButton}
                onPress={handlePreviousLesson}
                disabled={currentLesson === 0}
              >
                <LinearGradient
                  colors={currentLesson === 0 ? ['#e5e7eb', '#f3f4f6'] : gradients.info}
                  style={styles.controlButtonGradient}
                >
                  <MaterialCommunityIcons 
                    name="skip-previous" 
                    size={20} 
                    color={currentLesson === 0 ? "#9ca3af" : "#ffffff"} 
                  />
                  <Text style={[
                    styles.controlButtonText,
                    { color: currentLesson === 0 ? "#9ca3af" : "#ffffff" }
                  ]}>
                    ⏮️ Previous
                  </Text>
                </LinearGradient>
              </TouchableOpacity>

              <TouchableOpacity 
                style={styles.completeButton}
                onPress={handleMarkComplete}
              >
                <LinearGradient
                  colors={completedLessons.includes(currentLessonData?.id) ? gradients.success : courseData.gradient}
                  style={styles.completeButtonGradient}
                >
                  <MaterialCommunityIcons 
                    name={completedLessons.includes(currentLessonData?.id) ? "check-circle" : "check"} 
                    size={20} 
                    color="#ffffff" 
                  />
                  <Text style={styles.completeButtonText}>
                    {completedLessons.includes(currentLessonData?.id) ? '✅ Completed' : '✔️ Mark Complete'}
                  </Text>
                </LinearGradient>
              </TouchableOpacity>

              <TouchableOpacity 
                style={styles.controlButton}
                onPress={handleNextLesson}
                disabled={currentLesson === allLessons.length - 1}
              >
                <LinearGradient
                  colors={currentLesson === allLessons.length - 1 ? ['#e5e7eb', '#f3f4f6'] : gradients.warning}
                  style={styles.controlButtonGradient}
                >
                  <Text style={[
                    styles.controlButtonText,
                    { color: currentLesson === allLessons.length - 1 ? "#9ca3af" : "#ffffff" }
                  ]}>
                    ⏭️ Next
                  </Text>
                  <MaterialCommunityIcons 
                    name="skip-next" 
                    size={20} 
                    color={currentLesson === allLessons.length - 1 ? "#9ca3af" : "#ffffff"} 
                  />
                </LinearGradient>
              </TouchableOpacity>
            </View>
          </View>
        </Animated.View>

        {/* Enhanced Course Content */}
        <Animated.View 
          style={[
            styles.contentCard,
            {
              opacity: fadeAnim,
              transform: [{ translateY: slideAnim }],
            },
          ]}
        >
          <View style={styles.sectionHeader}>
            <LinearGradient
              colors={courseData.gradient}
              style={styles.sectionHeaderGradient}
            >
              <MaterialCommunityIcons name="book-open-variant" size={24} color="#ffffff" />
              <Text style={styles.sectionTitle}>📚 Course Content</Text>
            </LinearGradient>
          </View>
          
          {(courseData.modules || []).map((module, moduleIndex) => (
            <View key={module.id || moduleIndex} style={styles.moduleSection}>
              <View style={styles.moduleHeader}>
                <LinearGradient
                  colors={module.gradient || courseData.gradient}
                  style={styles.moduleIconGradient}
                >
                  <MaterialCommunityIcons name="folder-open" size={24} color="#ffffff" />
                </LinearGradient>
                <View style={styles.moduleInfo}>
                  <Text style={styles.moduleHeaderText}>
                    {module.title || `Module ${moduleIndex + 1}`}
                  </Text>
                  <Text style={styles.moduleDuration}>
                    ⏱️ {module.duration || 'Duration TBD'} • 📖 {(module.lessons || []).length} lessons
                  </Text>
                </View>
              </View>
              
              {(module.lessons || []).map((lesson, lessonIndex) => {
                const globalIndex = (courseData.modules || [])
                  .slice(0, moduleIndex)
                  .reduce((acc, mod) => acc + (mod.lessons || []).length, 0) + lessonIndex;
                
                const isCompleted = completedLessons.includes(lesson.id);
                const isCurrent = globalIndex === currentLesson;
                
                return (
                  <TouchableOpacity
                    key={lesson.id || `${moduleIndex}-${lessonIndex}`}
                    style={styles.lessonItem}
                    onPress={() => handleLessonSelect(globalIndex)}
                  >
                    {isCurrent && (
                      <LinearGradient
                        colors={courseData.gradient}
                        style={styles.currentLessonGradient}
                      />
                    )}
                    {isCompleted && (
                      <LinearGradient
                        colors={gradients.success}
                        style={styles.completedLessonGradient}
                      />
                    )}
                    
                    <View style={styles.lessonItemContent}>
                      <View style={styles.lessonItemLeft}>
                        <LinearGradient
                          colors={getLessonTypeGradient(lesson.type)}
                          style={styles.lessonItemIconGradient}
                        >
                          <MaterialCommunityIcons 
                            name={getLessonIcon(lesson.type)} 
                            size={16} 
                            color="#ffffff" 
                          />
                        </LinearGradient>
                        <View style={styles.lessonItemInfo}>
                          <Text style={[
                            styles.lessonItemTitle,
                            isCurrent && styles.currentLessonText,
                            isCompleted && styles.completedLessonText
                          ]}>
                            {lesson.title || `Lesson ${lessonIndex + 1}`}
                          </Text>
                          <Text style={styles.lessonItemDuration}>
                            ⏱️ {lesson.duration || 'Duration TBD'}
                          </Text>
                        </View>
                      </View>
                      
                      <View style={styles.lessonItemRight}>
                        {isCompleted && (
                          <LinearGradient
                            colors={gradients.success}
                            style={styles.statusIconGradient}
                          >
                            <MaterialCommunityIcons name="check" size={16} color="#ffffff" />
                          </LinearGradient>
                        )}
                        {isCurrent && !isCompleted && (
                          <LinearGradient
                            colors={courseData.gradient}
                            style={styles.statusIconGradient}
                          >
                            <MaterialCommunityIcons name="play" size={16} color="#ffffff" />
                          </LinearGradient>
                        )}
                        {!isCurrent && !isCompleted && (
                          <View style={styles.uncompletedIcon}>
                            <MaterialCommunityIcons name="circle-outline" size={20} color="#d1d5db" />
                          </View>
                        )}
                      </View>
                    </View>
                  </TouchableOpacity>
                );
              })}
            </View>
          ))}
        </Animated.View>

        {/* Enhanced Course Statistics */}
        <Animated.View 
          style={[
            styles.statsCard,
            {
              opacity: fadeAnim,
              transform: [{ scale: scaleAnim }],
            },
          ]}
        >
          <View style={styles.sectionHeader}>
            <LinearGradient
              colors={gradients.purple}
              style={styles.sectionHeaderGradient}
            >
              <MaterialCommunityIcons name="chart-line" size={24} color="#ffffff" />
              <Text style={styles.sectionTitle}>📊 Your Progress</Text>
            </LinearGradient>
          </View>
          <View style={styles.statsGrid}>
            <View style={styles.statItem}>
              <LinearGradient
                colors={gradients.orange}
                style={styles.statIconGradient}
              >
                <MaterialCommunityIcons name="book-open-variant" size={24} color="#ffffff" />
              </LinearGradient>
              <Text style={styles.statValue}>{allLessons.length}</Text>
              <Text style={styles.statLabel}>📚 Total Lessons</Text>
            </View>
            <View style={styles.statItem}>
              <LinearGradient
                colors={gradients.success}
                style={styles.statIconGradient}
              >
                <MaterialCommunityIcons name="check-circle" size={24} color="#ffffff" />
              </LinearGradient>
              <Text style={styles.statValue}>{completedLessons.length}</Text>
              <Text style={styles.statLabel}>✅ Completed</Text>
            </View>
            <View style={styles.statItem}>
              <LinearGradient
                colors={gradients.blue}
                style={styles.statIconGradient}
              >
                <MaterialCommunityIcons name="clock" size={24} color="#ffffff" />
              </LinearGradient>
              <Text style={styles.statValue}>{allLessons.length - completedLessons.length}</Text>
              <Text style={styles.statLabel}>⏳ Remaining</Text>
            </View>
            <View style={styles.statItem}>
              <LinearGradient
                colors={courseData.gradient}
                style={styles.statIconGradient}
              >
                <MaterialCommunityIcons name="trophy" size={24} color="#ffffff" />
              </LinearGradient>
              <Text style={styles.statValue}>{getProgressPercentage()}%</Text>
              <Text style={styles.statLabel}>🏆 Progress</Text>
            </View>
          </View>
        </Animated.View>

        {/* Quick Actions */}
        <Animated.View 
          style={[
            styles.quickActionsCard,
            {
              opacity: fadeAnim,
              transform: [{ translateY: slideAnim }],
            },
          ]}
        >
          <View style={styles.sectionHeader}>
            <LinearGradient
              colors={gradients.warning}
              style={styles.sectionHeaderGradient}
            >
              <MaterialCommunityIcons name="rocket-launch" size={24} color="#ffffff" />
              <Text style={styles.sectionTitle}>🚀 Quick Actions</Text>
            </LinearGradient>
          </View>
          <View style={styles.actionsGrid}>
            <TouchableOpacity
              style={styles.actionCard}
              onPress={() => navigation.navigate('LearningCoursesScreen')}
            >
              <LinearGradient
                colors={gradients.blue}
                style={styles.actionGradient}
              >
                <MaterialCommunityIcons name="book-multiple" size={24} color="#ffffff" />
                <Text style={styles.actionText}>📚 More Courses</Text>
              </LinearGradient>
            </TouchableOpacity>
            
            <TouchableOpacity
              style={styles.actionCard}
              onPress={() => navigation.navigate('LearningCertificateScreen')}
            >
              <LinearGradient
                colors={gradients.gold}
                style={styles.actionGradient}
              >
                <MaterialCommunityIcons name="certificate" size={24} color="#ffffff" />
                <Text style={styles.actionText}>🏆 Certificates</Text>
              </LinearGradient>
            </TouchableOpacity>
            
            <TouchableOpacity
              style={styles.actionCard}
              onPress={() => navigation.navigate('MyLearningHistoryScreen')}
            >
              <LinearGradient
                colors={gradients.success}
                style={styles.actionGradient}
              >
                <MaterialCommunityIcons name="history" size={24} color="#ffffff" />
                <Text style={styles.actionText}>📈 Progress</Text>
              </LinearGradient>
            </TouchableOpacity>
            
            <TouchableOpacity
              style={styles.actionCard}
              onPress={() => navigation.navigate('LearningDashboard')}
            >
              <LinearGradient
                colors={gradients.primary}
                style={styles.actionGradient}
              >
                <MaterialCommunityIcons name="view-dashboard" size={24} color="#ffffff" />
                <Text style={styles.actionText}>🏠 Dashboard</Text>
              </LinearGradient>
            </TouchableOpacity>
          </View>
        </Animated.View>
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: '#f0f4f8',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 8,
  },
  backButton: {
    padding: 8,
  },
  headerInfo: {
    flex: 1,
    alignItems: 'center',
    marginHorizontal: 16,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#ffffff',
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 1, height: 1 },
    textShadowRadius: 3,
  },
  headerSubtitle: {
    fontSize: 12,
    color: '#ffffff',
    marginTop: 4,
    opacity: 0.9,
  },
  menuButton: {
    padding: 8,
  },
  progressContainer: {
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 4,
  },
  progressGradient: {
    paddingHorizontal: 20,
    paddingVertical: 16,
  },
  progressInfo: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  progressLabel: {
    fontSize: 16,
    fontWeight: '600',
    color: '#ffffff',
  },
  progressPercentage: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#ffffff',
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 1, height: 1 },
    textShadowRadius: 3,
  },
  progressBar: {
    height: 10,
    backgroundColor: 'rgba(255, 255, 255, 0.3)',
    borderRadius: 5,
    marginBottom: 8,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    borderRadius: 5,
  },
  progressText: {
    fontSize: 14,
    color: '#ffffff',
    textAlign: 'center',
    opacity: 0.9,
  },
  container: {
    flex: 1,
    padding: 20,
  },
  welcomeCard: {
    marginBottom: 20,
    borderRadius: 20,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.25,
    shadowRadius: 12,
    elevation: 8,
  },
  welcomeGradient: {
    padding: 20,
    alignItems: 'center',
  },
  welcomeText: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#ffffff',
    marginTop: 12,
    marginBottom: 6,
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 1, height: 1 },
    textShadowRadius: 3,
  },
  welcomeSubtext: {
    fontSize: 14,
    color: '#ffffff',
    opacity: 0.9,
    textAlign: 'center',
  },
  currentLessonCard: {
    backgroundColor: '#ffffff',
    borderRadius: 20,
    marginBottom: 20,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.15,
    shadowRadius: 12,
    elevation: 8,
  },
  lessonHeaderGradient: {
    padding: 20,
  },
  lessonHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  lessonIconContainer: {
    width: 64,
    height: 64,
    borderRadius: 32,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    marginRight: 16,
  },
  lessonInfo: {
    flex: 1,
  },
  moduleTitle: {
    fontSize: 12,
    color: '#ffffff',
    marginBottom: 6,
    textTransform: 'uppercase',
    fontWeight: '600',
    opacity: 0.9,
  },
  lessonTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#ffffff',
    marginBottom: 6,
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 1, height: 1 },
    textShadowRadius: 3,
  },
  lessonDuration: {
    fontSize: 14,
    color: '#ffffff',
    opacity: 0.9,
  },
  lessonBadge: {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
  },
  lessonBadgeText: {
    fontSize: 10,
    fontWeight: '700',
    color: '#ffffff',
  },
  lessonContent: {
    padding: 20,
  },
  lessonDescription: {
    fontSize: 15,
    color: '#374151',
    lineHeight: 22,
    marginBottom: 20,
  },
  videoPlayer: {
    height: 220,
    borderRadius: 16,
    marginBottom: 20,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.25,
    shadowRadius: 8,
    elevation: 6,
  },
  videoPlayerGradient: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  playButtonContainer: {
    marginBottom: 16,
  },
  playButton: {
    width: 80,
    height: 80,
    borderRadius: 40,
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.25,
    shadowRadius: 8,
    elevation: 6,
  },
  videoTitle: {
    color: '#ffffff',
    fontSize: 18,
    fontWeight: '700',
    marginBottom: 6,
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 1, height: 1 },
    textShadowRadius: 3,
  },
  videoSubtitle: {
    color: '#d1d5db',
    fontSize: 14,
    opacity: 0.9,
  },
  lessonControls: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
  },
  controlButton: {
    flex: 0.3,
    borderRadius: 12,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 4,
  },
  controlButtonGradient: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    paddingHorizontal: 12,
  },
  completeButton: {
    flex: 0.4,
    borderRadius: 12,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.25,
    shadowRadius: 8,
    elevation: 6,
  },
  completeButtonGradient: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    paddingHorizontal: 12,
  },
  controlButtonText: {
    fontSize: 12,
    fontWeight: '600',
    marginHorizontal: 4,
  },
  completeButtonText: {
    color: '#ffffff',
    fontSize: 12,
    fontWeight: '700',
    marginHorizontal: 4,
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 1, height: 1 },
    textShadowRadius: 3,
  },
  contentCard: {
    backgroundColor: '#ffffff',
    borderRadius: 20,
    marginBottom: 20,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.15,
    shadowRadius: 12,
    elevation: 8,
  },
  sectionHeader: {
    borderRadius: 16,
    overflow: 'hidden',
    marginBottom: 20,
  },
  sectionHeaderGradient: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#ffffff',
    marginLeft: 12,
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 1, height: 1 },
    textShadowRadius: 3,
  },
  moduleSection: {
    marginBottom: 24,
    paddingHorizontal: 20,
  },
  moduleHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  moduleIconGradient: {
    width: 48,
    height: 48,
    borderRadius: 24,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.25,
    shadowRadius: 8,
    elevation: 6,
  },
  moduleInfo: {
    flex: 1,
  },
  moduleHeaderText: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1f2937',
    marginBottom: 4,
  },
  moduleDuration: {
    fontSize: 13,
    color: '#6b7280',
    fontWeight: '500',
  },
  lessonItem: {
    borderRadius: 12,
    marginBottom: 12,
    overflow: 'hidden',
    backgroundColor: '#f8fafc',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  currentLessonGradient: {
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    width: 4,
  },
  completedLessonGradient: {
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    width: 4,
  },
  lessonItemContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 16,
    paddingHorizontal: 16,
    paddingLeft: 20,
  },
  lessonItemLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  lessonItemIconGradient: {
    width: 36,
    height: 36,
    borderRadius: 18,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.15,
    shadowRadius: 4,
    elevation: 3,
  },
  lessonItemInfo: {
    flex: 1,
  },
  lessonItemTitle: {
    fontSize: 15,
    fontWeight: '600',
    color: '#1f2937',
    marginBottom: 4,
  },
  currentLessonText: {
    fontWeight: 'bold',
    color: '#3b82f6',
  },
  completedLessonText: {
    color: '#10b981',
  },
  lessonItemDuration: {
    fontSize: 12,
    color: '#6b7280',
    fontWeight: '500',
  },
  lessonItemRight: {
    marginLeft: 16,
  },
  statusIconGradient: {
    width: 24,
    height: 24,
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
  },
  uncompletedIcon: {
    width: 24,
    height: 24,
    justifyContent: 'center',
    alignItems: 'center',
  },
  statsCard: {
    backgroundColor: '#ffffff',
    borderRadius: 20,
    marginBottom: 20,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.15,
    shadowRadius: 12,
    elevation: 8,
  },
  statsGrid: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingBottom: 20,
  },
  statItem: {
    alignItems: 'center',
    flex: 1,
  },
  statIconGradient: {
    width: 56,
    height: 56,
    borderRadius: 28,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.25,
    shadowRadius: 8,
    elevation: 6,
  },
  statValue: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#1f2937',
    marginBottom: 6,
  },
  statLabel: {
    fontSize: 12,
    color: '#6b7280',
    textAlign: 'center',
    fontWeight: '500',
  },
  quickActionsCard: {
    backgroundColor: '#ffffff',
    borderRadius: 20,
    marginBottom: 20,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.15,
    shadowRadius: 12,
    elevation: 8,
  },
  actionsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingBottom: 20,
  },
  actionCard: {
    width: '48%',
    marginBottom: 16,
    borderRadius: 16,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 4,
  },
  actionGradient: {
    alignItems: 'center',
    padding: 20,
  },
  actionText: {
    fontSize: 14,
    fontWeight: '700',
    color: '#ffffff',
    marginTop: 8,
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 1, height: 1 },
    textShadowRadius: 3,
  },
});

export default CourseLearningScreen;

